import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { useField } from 'formik'
import { vars } from '@machobear/design-system/css'
import { FieldInput, MaskedInput } from '@/components/form'
import useMediaQuery from '@/hooks/use-media-query'
import { breakpoints, transitions, colors } from '@/styles'
import { validNumber, validExpirationDate, validCvv, generateCardMask } from '@/utils/payments'
import { ErrorText } from '@/components/form/error'
import iconAmericanExpress from './american-express.svg'
import iconMasterCard from './mastercard.svg'
import iconUnionPay from './unionpay.svg'
import iconVisa from './visa.svg'
import iconPlaceholder from './placeholder.svg'

const CardWrapper = styled.div`
  transition: ${transitions.floatingLabel};
  --label-offset: ${props => (props.shift ? '22px' : '17px')};
  position: absolute;
  left: 10px;
  top: 0;
  display: flex;
  justify-content: center;
  transform: translateY(var(--label-offset));
  height: 52px;
  pointer-events: none;
  overflow: hidden;
`

const CardImage = styled.img`
  image-rendering: pixelated;
  width: 32px;
  height: 21px;
  object-fit: contain;
`

const Wrapper = styled.div`
  --radius: 4px;
  --width: 1px;
  display: grid;
  grid-template-columns: 1fr 100px 100px;
  ${props =>
    (props.focused || props.error) &&
    `
    input[type] {
      border-color: ${!props.error ? colors.secondary : colors.red} !important;
    }
  `};

  input {
    border-color: ${vars.borderColor.field};
  }

  > div:not(:first-of-type) {
    input {
      border-top-left-radius: 0;
      border-bottom-left-radius: 0;
      border-left-width: 0;
    }
  }
  > div:not(:last-of-type) {
    input {
      border-top-right-radius: 0;
      border-bottom-right-radius: 0;
      border-right-width: 0;
    }
  }
  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    grid-template-columns: 1fr 1fr;
    > div:first-of-type {
      input {
        border-bottom-left-radius: 0;
        border-top-right-radius: var(--radius);
        border-right-width: var(--width);
      }
    }
    > div:nth-of-type(2) {
      input {
        border-top-width: 0;
        border-bottom-left-radius: var(--radius);
        border-left-width: var(--width);
      }
    }
    > div:last-of-type {
      input {
        border-top-width: 0;
        border-top-right-radius: 0;
        border-bottom-right-radius: var(--radius);
      }
    }
  }
`

const NumberWrapper = styled.div`
  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    grid-column: 1 / 2 span;
  }
`

const ExpiryWrapper = styled.div``

const CodeWrapper = styled.div``

// Prevents adding a 0 if any letter key
// is pressed
const isNumeric = value => {
  if (typeof value === 'number') return true
  if (typeof value !== 'string') return false
  const noSpace = value.replace(/\s/g, '')
  const num = Number(noSpace)
  return !isNaN(num)
}

// Automatically adds 0 for all numbers
// except 0 and 1.  Since it needs to be a month,
// its impossible for it to start with another number
// other than 0 and 1.
const smartExpiration = (...args) => {
  const [newState, prevState, current] = args
  const original = newState
  if (!current) return original
  const isAutofill = current.length > 1
  if (isAutofill) return original
  if (prevState.selection.start === 0 && prevState.selection.end === 0) {
    if (current !== '0' && current !== '1' && isNumeric(current)) {
      if (prevState.value.length > 0) return original
      const value = '0' + current
      const selection = { ...newState.selection }
      selection.start += 4
      selection.end = Math.max(selection.start, selection.end)
      return { selection, value }
    }
  }
  if (
    (prevState.selection.start === 1 && prevState.selection.end === 1) ||
    (prevState.selection.start === 0 && prevState.selection.end === 0)
  ) {
    if (current === '0' && prevState.value && prevState.value !== '1') {
      return prevState
    }
  }
  return original
}

export const cardTypes = {
  unknown: {
    name: 'unknown',
    flag: iconPlaceholder
  },
  visa: {
    name: 'Visa',
    flag: iconVisa
  },
  mastercard: {
    name: 'Mastercard',
    flag: iconMasterCard
  },
  'american-express': {
    name: 'Amex',
    flag: iconAmericanExpress
  },
  unionpay: {
    name: 'UnionPay',
    flag: iconUnionPay
  }
}

const CardNumber = ({
  cardMask,
  onFocus,
  onBlur,
  customError,
  children,
  autocompletePrefix,
  placeholder
}) => {
  const { t } = useTranslation()
  const displayMobile = useMediaQuery(`(max-width: ${breakpoints.ipadMiniMax})`)
  return (
    <NumberWrapper>
      <MaskedInput
        errorLocation={!displayMobile ? 'bottom' : 'top'}
        inputMode="numeric"
        htmlFor="creditcard"
        id="card_number"
        name="card_number"
        autoComplete={`${autocompletePrefix} cc-number`}
        format={cardMask}
        onBlur={onBlur}
        onFocus={onFocus}
        customError={customError}
        placeholder={
          !placeholder
            ? t('r.payment.edit.make_payment_card_number.label', 'Card number')
            : placeholder
        }
        required
        left={44}
      >
        {children}
      </MaskedInput>
    </NumberWrapper>
  )
}

const ExpriationDate = ({ cardInfo, expError, autocompletePrefix, ...rest }) => {
  const { t } = useTranslation()
  return (
    <ExpiryWrapper>
      <MaskedInput
        id="expiration_date"
        inputMode="numeric"
        name="expiration_date"
        autoComplete={`${autocompletePrefix} cc-exp`}
        format={'99 / 99'}
        placeholder={t('r.payment.edit.make_payment_expiration_date.label', 'MM/YY')}
        required
        beforeMaskedValueChange={smartExpiration}
        {...rest}
      />
      {expError && (
        <ErrorText role="alert">
          {t('r.payment.edit.invalid_expiration_date.error', 'Invalid expiration date')}
        </ErrorText>
      )}
    </ExpiryWrapper>
  )
}

const SecurityCode = ({ securityCodeError, autocompletePrefix, ...rest }) => {
  const { t } = useTranslation()
  return (
    <CodeWrapper>
      <FieldInput
        id="security_code"
        inputMode="numeric"
        name="security_code"
        autoComplete={`${autocompletePrefix} cc-csc`}
        maxLength={4}
        hasError={securityCodeError}
        placeholder={t('r.payment.edit.make_payment_security_code.label', 'CVC')}
        required
        {...rest}
      />
      {securityCodeError && (
        <ErrorText role="alert">
          {t('r.payment.edit.invalid_security_code.error', 'Invalid security code')}
        </ErrorText>
      )}
    </CodeWrapper>
  )
}

const CreditCardInput = ({
  autocompletePrefix = 'billing',
  setCardInfoValid,
  setCardMethodValid,
  placeholder,
  cardMethods
}) => {
  const { t } = useTranslation()
  const { acceptedCards, error: errorCard } = cardMethods
  const [cardValue, cardField] = useField('card_number')
  const [expValue, expField] = useField('expiration_date')
  const [codeValue, codeField] = useField('security_code')
  const cardInfo = {
    cardNumber: cardValue.value,
    expirationDate: expValue.value,
    securityCode: codeValue.value
  }
  const [cardType, setCardType] = useState('')
  const [cardMask, setCardMask] = useState('')
  const [isValidMethod, setIsValidMethod] = useState(false)
  const [expValid, setExpValid] = useState(false)
  const [cardValid, setCardValid] = useState(false)
  const [codeValid, setCodeValid] = useState(false)
  const [minCardLength, setMinCardLength] = useState(undefined)
  const [showCardError, setShowCardError] = useState(false)
  const [focus, setFocus] = useState({
    card_number: false,
    expiration_date: false,
    security_code: false
  })
  const onFocus = key => () => setFocus(rest => ({ ...rest, [key]: true }))
  const onBlur = key => () => setFocus(rest => ({ ...rest, [key]: false }))
  const anyFocus = focus.card_number || focus.expiration_date || focus.security_code

  // Check to Display Error Messages
  const showMethodError =
    cardInfo.cardNumber.length > 3 && acceptedCards !== '' && cardType !== '' && !isValidMethod

  const checkCardValidityOnBlur = () => {
    onBlur('card_number')()
    setShowCardError(!showMethodError && !cardValid)
  }

  useEffect(() => {
    if (!showMethodError && cardInfo.cardNumber.length >= minCardLength && !cardValid) {
      return setShowCardError(true)
    }
    return setShowCardError(false)
  }, [cardInfo.cardNumber, cardValid, minCardLength, setShowCardError, showMethodError])

  // Card Number Validation
  useEffect(() => {
    const { isValid: isCardValid, card } = validNumber(cardInfo.cardNumber)
    const { isValid: isExpValid } = validExpirationDate(cardInfo.expirationDate)
    const { isValid: isCodeValid } = validCvv(cardInfo.securityCode, card?.code.size)

    setMinCardLength(card?.lengths[0])
    setCardValid(isCardValid)
    setExpValid(isExpValid)
    setCodeValid(isCodeValid)
    setCardInfoValid(isCardValid && isExpValid && isCodeValid)
  }, [setCardInfoValid, cardInfo.cardNumber, cardInfo.expirationDate, cardInfo.securityCode])

  // Card Method Validation
  useEffect(() => {
    const { card = {} } = validNumber(cardInfo.cardNumber)
    const isIncluded = acceptedCards.includes(card?.type)
    setIsValidMethod(isIncluded)
    setCardMethodValid(isIncluded)
  }, [cardInfo.cardNumber, setCardMethodValid])

  // Save Card Type
  useEffect(() => {
    if (!cardInfo.cardNumber) {
      setCardType('')
      setCardMask('')
      return
    }
    const { card } = validNumber(cardInfo.cardNumber)
    if (card) {
      setCardMask(generateCardMask(card))
    }
    setCardType(card?.type)
  }, [cardType, cardInfo.cardNumber])

  // Set Card Image
  const { flag: flagIcon = null } = cardTypes[cardType] || {}

  const getCustomError = () => {
    if (!cardField.touched) return false
    if (showMethodError) return errorCard
    if (showCardError) return t('r.payment.edit.invalid_card_number.error', 'Invalid card number')
    return false
  }
  const isEmpty = !cardInfo.cardNumber
  const securityCodeError = codeField.touched && !codeField.error && !codeValid
  const expError = expField.touched && !expField.error && !expValid
  const hasError =
    getCustomError() ||
    expError ||
    securityCodeError ||
    (cardField.error && cardField.touched) ||
    (expField.error && expField.touched) ||
    (codeField.error && codeField.touched)
  const cardFieldFilled = !isEmpty || focus.card_number
  return (
    <Wrapper focused={anyFocus} error={hasError}>
      <CardNumber
        autocompletePrefix={autocompletePrefix}
        customError={getCustomError()}
        cardMask={cardMask}
        onBlur={checkCardValidityOnBlur}
        onFocus={onFocus('card_number')}
        placeholder={!cardFieldFilled ? placeholder : false}
      >
        <CardWrapper shift={cardFieldFilled}>
          <CardImage src={!!flagIcon ? flagIcon : iconPlaceholder} alt="" />
        </CardWrapper>
      </CardNumber>
      <ExpriationDate
        autocompletePrefix={autocompletePrefix}
        cardInfo={cardInfo}
        expError={expError}
        onFocus={onFocus('expiration_date')}
        onBlur={onBlur('expiration_date')}
      />
      <SecurityCode
        autocompletePrefix={autocompletePrefix}
        securityCodeError={securityCodeError}
        onFocus={onFocus('security_code')}
        onBlur={onBlur('security_code')}
      />
    </Wrapper>
  )
}

export default CreditCardInput
